import { useClickOutside } from '@on3/ui-lib/hooks/useClickOutside';
import clsx from 'clsx';
import { usePathname } from 'next/navigation';
import { type HTMLAttributes, useEffect, useRef, useState } from 'react';

import styles from './Popover.module.scss';

interface Position {
  top?: string;
  bottom?: string;
  left?: string;
  right?: string;
}

interface WrapperProps extends HTMLAttributes<HTMLDivElement> {
  controlRef: React.RefObject<HTMLButtonElement>;
  children: React.ReactNode;
}

interface PopoverProps {
  children: (props: {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    Wrapper: ({ children }: WrapperProps) => JSX.Element | null;
  }) => React.ReactNode;
  className?: string;
}

const Wrapper = ({
  controlRef: ref,
  children,
  style = {},
  ...props
}: WrapperProps) => {
  const [position, setPosition] = useState<Position | null>(null);

  useEffect(() => {
    if (!ref?.current) return;

    const calculatePosition = () => {
      const element = ref.current;

      if (!element) return;

      const rect = element.getBoundingClientRect();

      const { top, left } = rect;
      const centerX = window.innerWidth / 2;
      const centerY = window.innerHeight / 2;

      // Determine the popover position
      const newPosition: Position = {};

      if (left < centerX) newPosition.left = '0';
      else newPosition.right = '0';

      if (top < centerY) newPosition.top = '100%';
      else newPosition.bottom = '100%';

      setPosition(newPosition);
    };

    // Calculate position on mount
    calculatePosition();
    window.addEventListener('resize', calculatePosition);

    return () => {
      window.removeEventListener('resize', calculatePosition);
    };
  }, [ref]);

  if (!position) return null;

  return (
    <div style={{ ...position, ...style }} {...props}>
      {children}
    </div>
  );
};

export const Popover = ({ children, className }: PopoverProps) => {
  const pathname = usePathname();

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const ref = useRef<HTMLDivElement>(null);

  useClickOutside(ref, () => {
    setIsOpen(false);
  });

  useEffect(() => {
    setIsOpen(false);
  }, [pathname]);

  return (
    <div className={clsx(styles.block, className)} ref={ref}>
      {children({ isOpen, setIsOpen, Wrapper })}
    </div>
  );
};
